import { useAppDispatch, useAppTranslation } from 'app/hooks';
import WarningIcon from 'assets/icons/WarningIcon';
import { showConfirmModal, showInfoModal } from 'components/Modals/CommonModals';
import { onChangeAuthPage } from 'features/home/homeSlice';
import useUserDetail from 'hooks/useUserDetail';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ConditionButtonProps {
  className?: string;
  content: React.ReactNode;
  onClick: () => void;
  onDoubleClick?: () => void;
  disabled?: boolean;
  name?: string;
  isSkipCheckOnClick?: boolean;
  testerCheck?: boolean;
  skipKyc?: boolean;
}

const ConditionButton: React.FunctionComponent<ConditionButtonProps> = (props) => {
  const {
    className = '',
    content,
    onClick,
    disabled = false,
    name,
    onDoubleClick,
    isSkipCheckOnClick = false,
    testerCheck = false,
    skipKyc = false,
  } = props;
  const { currentUser, isUserLoggedIn } = useUserDetail();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const t = useAppTranslation();

  const handleOnClick = () => {
    if (isSkipCheckOnClick) {
      onClick();
      return;
    }
    if (!isUserLoggedIn) {
      dispatch(onChangeAuthPage(0));
      return;
    }
    if (skipKyc || (testerCheck && currentUser?.isTester)) {
      onClick();
      return;
    }
    if (
      !currentUser?.kyc ||
      currentUser.kyc.status === 'INPROGRESS' ||
      currentUser.kyc.status === 'RETRY'
    ) {
      showConfirmModal({
        title: t('popup.kyc.title.required'),
        content: <p>{t('popup.kyc.message.kycRequired')}</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => {
          navigate('/kyc');
        },
        okText: t('popup.buttons.ok'),
        cancelText: t('popup.buttons.cancel'),
      });
      return;
    }
    if (currentUser.kyc.status === 'REJECTED') {
      showInfoModal({
        title: t('popup.kyc.title.required'),
        content: <p>{t('popup.kyc.message.rejected')}</p>,
        width: 360,
        icon: <WarningIcon />,
      });
      return;
    }
    if (currentUser.kyc.status === 'PENDING') {
      showInfoModal({
        title: t('popup.kyc.title.required'),
        content: <p>{t('popup.kyc.message.underReview')}</p>,
        width: 360,
        icon: <WarningIcon />,
      });
      return;
    }

    onClick();
  };

  const handleOnDoubleClick = () => {
    if (!isUserLoggedIn) {
      dispatch(onChangeAuthPage(0));
      return;
    }

    if (testerCheck && currentUser?.isTester) {
      onDoubleClick && onDoubleClick();
      return;
    }

    if (
      !currentUser?.kyc ||
      currentUser.kyc.status === 'INPROGRESS' ||
      currentUser.kyc.status === 'RETRY'
    ) {
      showConfirmModal({
        title: 'KYC Verification Required',
        content: <p>Please complete KYC to access this feature.</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => {
          navigate('/kyc');
        },
        okText: 'Verify KYC',
        cancelText: 'Skip',
      });
      return;
    }
    if (currentUser.kyc.status === 'REJECTED') {
      showInfoModal({
        title: 'KYC Verification Required',
        content: <p>Your KYC has been rejected. Please contact our support team.</p>,
        width: 360,
        icon: <WarningIcon />,
      });
      return;
    }
    if (currentUser.kyc.status === 'PENDING') {
      showInfoModal({
        title: 'KYC Verification Required',
        content: <p>Your KYC is under review.</p>,
        width: 360,
        icon: <WarningIcon />,
      });
      return;
    }
    onDoubleClick && onDoubleClick();
  };

  return (
    <button
      className={className}
      name={name}
      onClick={handleOnClick}
      onDoubleClick={handleOnDoubleClick}
      disabled={disabled}
      type="button"
    >
      {content}
    </button>
  );
};

export default ConditionButton;
